import type { ReactNode } from "react";
import { Button, Modal } from "@/deps/ui-library";

/**
 * A modal dialog, built from RDS `Modal` parts (Ark UI's dialog underneath).
 *
 * Ark does the four things people get wrong by hand: the focus trap, the inert background, a layer
 * nothing on the page can cover (it is portalled to the body), and Escape. The frame is the one
 * RDS's `Modal.Form` / `Modal.View` presets use — a title and a line under it, a body that scrolls
 * on its own, Cancel on the right — but the actions are the caller's, because a dialog here is as
 * often a confirmation with two outcomes (Verify / Return) as it is a form.
 */
export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  /** The word on the button that closes without doing anything. "Cancel" is usually right. */
  dismissLabel = "Cancel",
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  dismissLabel?: string;
}) {
  return (
    <Modal.Root
      open={open}
      // Escape, the close button and a click on the backdrop all arrive here, so the parent's state
      // follows the dialog rather than the other way round.
      onOpenChange={(details) => {
        if (!details.open) onClose();
      }}
      // A click on the backdrop closes it, as it always has here. A click inside the content does
      // not — closing on that would lose whatever was typed.
      closeOnInteractOutside
      lazyMount
      unmountOnExit
      trapFocus
    >
      <Modal.Portal>
        <Modal.Backdrop />
        <Modal.Positioner centered scrollMode="inside">
          <Modal.Content size="lg" scrollMode="inside">
            <div className="pr-8">
              <Modal.Title size="sm">{title}</Modal.Title>
              {description ? <Modal.Description size="sm">{description}</Modal.Description> : null}
            </div>

            <Modal.Body className="-mx-1 flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-1 pb-1">
              {children}
            </Modal.Body>

            <Modal.Footer align="right">
              <Button colorScheme="default" variant="outline" size="sm" onClick={onClose}>
                {dismissLabel}
              </Button>
              {footer}
            </Modal.Footer>

            <Modal.CloseTrigger aria-label={`Close ${title}`} />
          </Modal.Content>
        </Modal.Positioner>
      </Modal.Portal>
    </Modal.Root>
  );
}
